/**
 * Photo PATCH outbox — fire-and-forget writes with deferred sync.
 *
 * Field photographers operate on flaky connections; awaiting every PATCH
 * blocks the UI and forces them to wait on round-trips. The outbox lets
 * callers update local state synchronously and queue a server PATCH that
 * is debounced + retried in the background. The user only blocks at
 * synchronisation points — the most important being "Mark Done", where
 * `flushPendingForPhoto(imageId)` must succeed before the photo can be
 * marked complete (otherwise the just-set fields would be lost).
 *
 * Exposed:
 *   - queuePhotoPatch(imageId, fields)        // optimistic, no await
 *   - flushPendingForPhoto(imageId)           // await before Mark Done
 *   - hasPendingForPhoto(imageId)             // for badge / disable UI
 *
 * Mechanics:
 *   - Per-photo slot holds merged pending fields, debounce timer,
 *     in-flight promise, retry counter.
 *   - Multiple field updates within the debounce window collapse into
 *     one PATCH (latest wins per field).
 *   - Failed PATCH re-merges the fields back into the queue and
 *     reschedules with exponential backoff (capped at 30 s). Only for a
 *     network error, a 5xx, a 401, a 408 or a 429: any other 4xx is the
 *     server saying no, and asking again will not change that. That op is
 *     dropped and reported through `subscribePhotoOutboxRejections`, so it
 *     can never block Mark Done.
 *   - The result of the latest successful PATCH is delivered via
 *     `onResolve(imageId, response)` if registered, so callers can
 *     reconcile derived fields like `rendered_caption` when the
 *     network finally cooperates.
 */

import axios from 'axios';
import { API_BASE } from './url';

const DEFAULT_DEBOUNCE_MS = 600;
const MAX_BACKOFF_MS = 30_000;

// One slot per (imageId, endpointKind). Different endpoint kinds talk
// to different URLs ('generic' = PATCH /api/photos/<id>/, 'caption' =
// PATCH /api/photos/<id>/caption/, 'keywords' = PATCH /api/photos/<id>/
// keywords/). Each kind has its own debounce + retry state.
const slots = new Map();
const subscribers = new Set();
const rejectionSubscribers = new Set();

const FIELD_LABELS = {
  primary_subject_person_id: 'primary subject',
  person_order: 'name order',
};

// Not final, though 4xx: a timeout and a rate limit pass, and a 401 means the
// session ran out. The keep-alive sends the editor to the login page and the
// queued op flushes once the session is back (Mark Done waits for that).
const RETRYABLE_4XX = new Set([401, 408, 429]);

function isRefusal(err) {
  const status = err?.response?.status;
  return (
    typeof status === 'number' && status >= 400 && status < 500 && !RETRYABLE_4XX.has(status)
  );
}

function rejectionMessage(fields, data) {
  const keys = Object.keys(fields);
  const reasons = keys
    .flatMap((k) => (data && typeof data === 'object' ? [].concat(data[k] ?? []) : []))
    .filter((r) => typeof r === 'string' && r);
  const what = keys.map((k) => FIELD_LABELS[k] || k).join(', ');
  return `Not saved (${what}): ${reasons.join(' ') || 'refused by the server.'}`;
}

/**
 * Subscribe to refused PATCHes (a 4xx answer). The op is already dropped;
 * `fn(imageId, { fields, status, data, message })` is for showing it.
 * Returns an unsubscribe fn.
 */
export function subscribePhotoOutboxRejections(fn) {
  rejectionSubscribers.add(fn);
  return () => rejectionSubscribers.delete(fn);
}

function notifyRejection(imageId, rejection) {
  for (const fn of rejectionSubscribers) {
    try {
      fn(imageId, rejection);
    } catch (e) {
      console.error('photoOutbox rejection subscriber threw', e);
    }
  }
}

const ENDPOINT_PATHS = {
  generic: (imageId) => `${API_BASE}/photos/${imageId}/`,
  caption: (imageId) => `${API_BASE}/photos/${imageId}/caption/`,
  keywords: (imageId) => `${API_BASE}/photos/${imageId}/keywords/`,
};

function slotKey(imageId, kind) {
  return `${imageId}|${kind}`;
}

function notifySubscribers(imageId, response) {
  for (const fn of subscribers) {
    try {
      fn(imageId, response);
    } catch (e) {
      console.error('photoOutbox subscriber threw', e);
    }
  }
}

/**
 * Subscribe to successful PATCH responses. Returns an unsubscribe fn.
 * Useful for components that need to reconcile derived fields
 * (`rendered_caption`, etc.) once the server confirms.
 */
export function subscribePhotoOutbox(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

function getOrCreateSlot(imageId, kind) {
  const key = slotKey(imageId, kind);
  let slot = slots.get(key);
  if (!slot) {
    slot = {
      imageId,
      kind,
      pendingFields: {},
      timer: null,
      inflight: null,
      retries: 0,
    };
    slots.set(key, slot);
  }
  return slot;
}

async function flushSlot(imageId, kind) {
  const slot = slots.get(slotKey(imageId, kind));
  if (!slot) return null;
  slot.timer = null;
  const fields = slot.pendingFields;
  slot.pendingFields = {};
  const fieldKeys = Object.keys(fields);
  if (fieldKeys.length === 0) return null;
  // Coalesce concurrent flushes: if one's already in flight, wait for it
  // first so we don't race two PATCHes that mutate the same row.
  if (slot.inflight) {
    try {
      await slot.inflight;
    } catch {
      /* fall through; we'll retry our own pending set anyway */
    }
  }
  const buildUrl = ENDPOINT_PATHS[kind] || ENDPOINT_PATHS.generic;
  const promise = axios
    .patch(buildUrl(imageId), fields)
    .then((res) => {
      slot.retries = 0;
      slot.inflight = null;
      notifySubscribers(imageId, res.data);
      return res.data;
    })
    .catch((err) => {
      slot.inflight = null;
      if (isRefusal(err)) {
        // Final: drop the op and report it. Re-merging it kept every later
        // change in the same doomed PATCH and made Mark Done's flush throw.
        slot.retries = 0;
        const { status, data } = err.response;
        notifyRejection(imageId, { fields, status, data, message: rejectionMessage(fields, data) });
        return null;
      }
      // Re-merge our fields back into the queue (NEWER fields take
      // precedence — we wrote into the same map while inflight, so
      // only re-merge keys that aren't already pending).
      for (const k of fieldKeys) {
        if (!(k in slot.pendingFields)) {
          slot.pendingFields[k] = fields[k];
        }
      }
      const delay = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** Math.min(slot.retries, 5));
      slot.retries += 1;
      if (slot.timer) clearTimeout(slot.timer);
      slot.timer = setTimeout(() => {
        flushSlot(imageId, kind).catch(() => {});
      }, delay);
      throw err;
    });
  slot.inflight = promise;
  return promise;
}

/**
 * Optimistic, fire-and-forget PATCH. Caller updates local state itself.
 *
 * @param {string} imageId
 * @param {object} fields  partial Photo fields to PATCH
 * @param {object} [opts]
 * @param {number} [opts.debounceMs]  override debounce (default 600)
 * @param {'generic'|'caption'|'keywords'} [opts.endpoint]  which API
 *   path to PATCH. Defaults to 'generic' (PATCH /api/photos/<id>/).
 *   Each kind has its own debounce timer + retry state, so caption
 *   and order updates don't block one another.
 */
export function queuePhotoPatch(imageId, fields, opts = {}) {
  if (!imageId || !fields || typeof fields !== 'object') return;
  const kind = opts.endpoint || 'generic';
  const slot = getOrCreateSlot(imageId, kind);
  Object.assign(slot.pendingFields, fields);
  if (slot.timer) clearTimeout(slot.timer);
  const debounceMs = typeof opts.debounceMs === 'number' ? opts.debounceMs : DEFAULT_DEBOUNCE_MS;
  slot.timer = setTimeout(() => {
    flushSlot(imageId, kind).catch(() => {
      /* errors logged by retry path */
    });
  }, debounceMs);
}

function* slotsForPhoto(imageId) {
  const prefix = `${imageId}|`;
  for (const [key, slot] of slots) {
    if (key.startsWith(prefix)) yield slot;
  }
}

/**
 * Block until the photo's outbox is empty across ALL endpoint kinds.
 * Awaited at sync points (Mark Done, photo switch). Throws if any
 * final flush fails — caller decides whether to abort the action.
 */
export async function flushPendingForPhoto(imageId) {
  if (!imageId) return;
  const slotList = Array.from(slotsForPhoto(imageId));
  if (slotList.length === 0) return;
  const flushes = [];
  for (const slot of slotList) {
    if (slot.timer) {
      clearTimeout(slot.timer);
      slot.timer = null;
    }
    const hasPending = Object.keys(slot.pendingFields).length > 0;
    if (hasPending) {
      flushes.push(flushSlot(slot.imageId, slot.kind));
    } else if (slot.inflight) {
      flushes.push(slot.inflight);
    }
  }
  await Promise.all(flushes);
}

export function hasPendingForPhoto(imageId) {
  if (!imageId) return false;
  for (const slot of slotsForPhoto(imageId)) {
    if (Object.keys(slot.pendingFields).length > 0 || slot.inflight) return true;
  }
  return false;
}
